import { Button } from "@studyplus/boron-ui";
import format from "date-fns/format";
import { useFormik } from "formik";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { StylesConfig } from "react-select";
import * as yup from "yup";
import { BlockRow } from "../../components/atoms/BlockRow/index";
import ErrorText from "../../components/atoms/ErrorText";
import { Flex } from "../../components/atoms/Flex";
import Label from "../../components/atoms/Label";
import {
  MainSection,
  PageHeader,
} from "../../components/atoms/MainSection/index";
import { SingleDatePickerField } from "../../components/atoms/SingleDatePickerField/index";
import { SelectWrapper } from "../../components/general/SelectWrapper/index";
import {
  customStyles as defaultStyles,
  OptionType,
} from "../../components/general/SelectWrapper/styles";
import { LectureAttendanceWithSchedule } from "../../domains/LectureAttendance";
import { useFlashMessage } from "../../hooks/useFlashMessage";
import SectionInterface from "../../interfaces/SectionInterface";
import styles from "./SectionsLectureTransferForm.styles.scss";
import { useFetchLectureSessions } from "./useFetchLectureSessions";
import { useMutateLectureTransfer } from "./useMutateLectureTransfer";

// NOTE: 将来的には対象生徒と振替元も編集可能にする想定。
// そのため、振替元を編集可能かどうかのフラグ(isOriginEditable)をもらえるようにしていますが、現状は利用していません。
// 今後編集可能にする場合は、対象生徒と振替元講座予定のリストを取得して保持する必要があります。
type Props = {
  section: SectionInterface;
  isOriginEditable: boolean;
  originLectureAttendance: LectureAttendanceWithSchedule;
};

const valueContainerStyles: StylesConfig<OptionType>["valueContainer"] = (
  styles,
  state,
) => {
  return defaultStyles.valueContainer === undefined
    ? styles
    : {
        ...defaultStyles.valueContainer(styles, state),

        // NOTE: 通常はisDisabled時に50%透過するが、ここではテキストを表示したいため例外的に透過しない
        opacity: 1,
      };
};
const customStyles = { valueContainer: valueContainerStyles };

export const SectionsLectureTransferForm = ({
  isOriginEditable,
  originLectureAttendance,
  section,
}: Props) => {
  const form = useForm({ originLectureAttendance, section });

  return (
    <MainSection>
      <PageHeader>振替受講の登録</PageHeader>
      <BlockRow marginTop="2.4rem">
        <form onSubmit={form.handleSubmit} aria-label="振替受講の登録">
          {/* 対象生徒 */}
          <Label isMute>対象生徒</Label>
          <SelectWrapper
            isDisabled={!isOriginEditable}
            options={form.targetStudentOptions}
            value={form.targetStudentOptions.find(
              (option) => option.value === form.values.targetStudentId,
            )}
            size="md"
            fontSize="md"
            styles={customStyles}
          />

          {/* 振替元講座予定 */}
          <BlockRow marginTop="1.6rem">
            <Label isMute noMargin>
              振替元講座予定
            </Label>
            <Flex gap={"1"} alignItems="flex-start" marginTop="0.8rem">
              <SingleDatePickerField
                className={styles.datePicker}
                disabled={!isOriginEditable}
                value={form.values.originLectureSessionHeldAt || new Date()}
                dateFormat="yyyy年M月d日"
              />
              <div className={styles.selectBoxContainer}>
                <SelectWrapper
                  isDisabled={!isOriginEditable}
                  options={form.originLectureSessionOptions}
                  value={form.originLectureSessionOptions.find(
                    (option) =>
                      option.value === form.values.originLectureSessionId,
                  )}
                  size="md"
                  fontSize="md"
                  placeholder="振替元講座予定を選択してください"
                  noOptionsMessage={() => "講座予定がありません"}
                  className={styles.selectBox}
                  styles={customStyles}
                />
                <ErrorText>{form.errors.originLectureSessionId}</ErrorText>
              </div>
            </Flex>
          </BlockRow>

          {/* 振替先講座予定 */}
          <BlockRow marginTop="1.6rem">
            <Label isMute htmlFor="destinationLectureSession" noMargin>
              振替先講座予定
            </Label>
            <Flex gap={"1"} alignItems="flex-start" marginTop="0.8rem">
              <SingleDatePickerField
                className={styles.datePicker}
                value={
                  form.values.destinationLectureSessionHeldAt || new Date()
                }
                dateFormat="yyyy年M月d日"
                onDateChange={(date) => {
                  form.setFieldValue("destinationLectureSessionHeldAt", date);
                }}
              />
              <div className={styles.selectBoxContainer}>
                <SelectWrapper
                  inputId="destinationLectureSession"
                  options={form.destinationLectureSessionOptions}
                  value={
                    form.destinationLectureSessionOptions.find(
                      (option) =>
                        option.value ===
                        form.values.destinationLectureSessionId,
                    ) || null
                  }
                  size="md"
                  placeholder="振替先講座予定を選択してください"
                  noOptionsMessage={() => "講座予定がありません"}
                  onChange={(option: any) => {
                    form.setFieldValue(
                      "destinationLectureSessionId",
                      option?.value,
                    );
                  }}
                  className={styles.selectBox}
                  fontSize="md"
                  styles={customStyles}
                />
                <ErrorText>{form.errors.destinationLectureSessionId}</ErrorText>
              </div>
            </Flex>
          </BlockRow>

          <Flex marginTop="2rem" justifyContent="flex-end">
            {/* 登録 */}
            <Button
              type="submit"
              isLoading={form.isPending}
              disabled={form.isPending || !form.isValid}
            >
              登録
            </Button>
          </Flex>
        </form>
      </BlockRow>
    </MainSection>
  );
};

type Values = {
  targetStudentId: string | null;
  originLectureSessionId: string;
  originLectureSessionHeldAt: Date;
  destinationLectureSessionId: string;
  destinationLectureSessionHeldAt: Date;
};
const buildInitialValues = ({
  originLectureAttendance,
}: Pick<FormProps, "originLectureAttendance">): Values => {
  return {
    targetStudentId: originLectureAttendance.student.id,
    originLectureSessionId: originLectureAttendance.lectureSessionId,
    originLectureSessionHeldAt: originLectureAttendance.schedule.startAt,
    destinationLectureSessionId: "",
    destinationLectureSessionHeldAt: new Date(),
  };
};
const validationSchema = yup.object().shape({
  targetStudentId: yup.string().required("対象生徒を選択してください"),
  originLectureSessionId: yup
    .string()
    .trim()
    .required("振替元講座予定を選択してください"),
  destinationLectureSessionId: yup
    .string()
    .trim()
    .required("振替先講座予定を選択してください"),
});

type FormProps = Pick<Props, "originLectureAttendance" | "section">;
const useForm = ({ originLectureAttendance, section }: FormProps) => {
  const { mutate, isPending } = useMutateLectureTransfer({
    sectionId: section.id,
  });
  const { showSuccessMessage, showErrorMessage } = useFlashMessage();
  const navigate = useNavigate();
  const formik = useFormik<Values>({
    initialValues: buildInitialValues({ originLectureAttendance }),
    validationSchema,
    onSubmit(values: Values) {
      if (
        values.targetStudentId &&
        values.originLectureSessionId &&
        values.destinationLectureSessionId
      ) {
        mutate(
          {
            studentId: values.targetStudentId,
            originLectureSessionId: values.originLectureSessionId,
            destinationLectureSessionId: values.destinationLectureSessionId,
          },
          {
            onSuccess(response) {
              showSuccessMessage("振替受講を登録しました");
              navigate(
                `/sections/${section.id}/lecture_sessions/${response.lectureAttendance.originLectureSessionId}/attendees`,
              );
            },
            onError() {
              showErrorMessage("振替受講を登録できませんでした");
            },
          },
        );
      }
    },
  });

  const { data: lectureSessions } = useFetchLectureSessions({
    sectionId: section.id,
    transferOriginAttendanceId: originLectureAttendance.id,
    dateString: format(
      formik.values.destinationLectureSessionHeldAt ?? new Date(),
      "yyyy-MM-dd",
    ),
  });

  const targetStudentOptions = [
    {
      label: originLectureAttendance.student.fullName,
      value: originLectureAttendance.student.id,
    },
  ];

  const originLectureSessionOptions = [
    {
      label: originLectureAttendance.schedule.summary,
      value: originLectureAttendance.lectureSessionId,
    },
  ];

  const destinationLectureSessionOptions = lectureSessions
    ? lectureSessions.map((lectureSession) => ({
        label: lectureSession.schedule.summary,
        value: lectureSession.id,
      }))
    : [];

  React.useEffect(() => {
    formik.setFieldValue("destinationLectureSessionId", "", false);
  }, [formik.values.destinationLectureSessionHeldAt]);

  return {
    ...formik,
    targetStudentOptions,
    originLectureSessionOptions,
    destinationLectureSessionOptions,
    isPending,
  };
};
